import React, {useState} from 'react';
import Modal from '../../Modal';

const MAX_RESPONSE = 2000;

// Keys mirror the storefront order-review form (server/views/storefront/order.ejs),
// which is the only real producer of survey data. Unknown/legacy values fall
// back to a de-kebabed version of the raw string.
const SURVEY_REASON_LABELS = {
  'poor-quality': 'Poor product quality',
  'not-as-described': 'Not as described in listing',
  'custom-not-as-ordered': "Custom order wasn't as ordered",
  'shipping-issues': 'Major shipping issues',
  other: 'Other problem',
};

function surveyReasonLabel(reason) {
  return SURVEY_REASON_LABELS[reason] || String(reason).replace(/-/g, ' ');
}

// The form submits contactedSeller as the string 'yes'/'no'; tolerate a legacy
// boolean too.
function contactedSeller(value) {
  return value === 'yes' || value === true;
}

function recommendBadge(rating) {
  if (rating === 1) {
    return <span className="seller-reviews__badge seller-reviews__badge--positive">Would recommend</span>;
  }
  if (rating === 0) {
    return (
      <span className="seller-reviews__badge seller-reviews__badge--negative">
        Would not recommend
      </span>
    );
  }
  return <span className="seller-reviews__badge seller-reviews__badge--neutral">No rating</span>;
}

/**
 * Full review details plus the public response form. `onSubmitResponse` returns a
 * promise; the parent updates the review after the server accepts it.
 */
export default function ReviewDetailModal({review, onClose, onSubmitResponse}) {
  const [response, setResponse] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');

  if (!review) return null;

  const alreadyResponded = Boolean(review.response);
  const survey = review.survey || null;
  const hasSurvey = survey && (survey.reason || (survey.contactedSeller != null && survey.contactedSeller !== ''));

  const handleSubmit = async event => {
    event.preventDefault();
    const trimmed = response.trim();
    if (!trimmed) {
      setError('Enter a response before submitting.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await onSubmitResponse(review, trimmed);
      setSubmitting(false);
      setEditing(false);
    } catch (submitError) {
      setError(submitError.message || 'Failed to submit response.');
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Review details"
      size="large"
      className="seller-reviews__modal"
      ariaLabelledBy="seller-reviews-modal-title"
    >
      <div className="seller-reviews__detail-section">
        <div className="seller-reviews__detail-label">Rating</div>
        {recommendBadge(review.rating)}
      </div>

      <div className="seller-reviews__detail-section">
        <div className="seller-reviews__detail-label">Customer</div>
        <div>{review.customer?.nickname || 'Customer'}</div>
      </div>

      <div className="seller-reviews__detail-section">
        <div className="seller-reviews__detail-label">Product</div>
        <div>{review.Product?.name || '—'}</div>
      </div>

      {review.Order?.uuid && (
        <div className="seller-reviews__detail-section">
          <div className="seller-reviews__detail-label">Order</div>
          <a href={`/account/orders/${review.Order.uuid}`}>{review.Order.uuid}</a>
        </div>
      )}

      <div className="seller-reviews__detail-section">
        <div className="seller-reviews__detail-label">Comment</div>
        <div>{review.comment || 'No comment provided.'}</div>
      </div>

      {hasSurvey && (
        <div className="seller-reviews__detail-section">
          <div className="seller-reviews__detail-label">Survey</div>
          <ul>
            {survey.reason && (
              <li>Reason: {surveyReasonLabel(survey.reason)}</li>
            )}
            {survey.contactedSeller != null && survey.contactedSeller !== '' && (
              <li>Contacted seller: {contactedSeller(survey.contactedSeller) ? 'Yes' : 'No'}</li>
            )}
          </ul>
        </div>
      )}

      <div className="seller-reviews__detail-section seller-reviews__response-form">
        <div className="seller-reviews__detail-label">Your public response</div>
        {alreadyResponded && !editing ? (
          <>
            <p>{review.response}</p>
            {review.responseEditedAt && <small>Edited {new Date(review.responseEditedAt).toLocaleDateString()}</small>}
            <button type="button" className="button secondary" onClick={() => { setResponse(review.response); setEditing(true); setError(''); }}>Edit response</button>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <textarea
              value={response}
              maxLength={MAX_RESPONSE}
              onChange={event => setResponse(event.target.value)}
              aria-label="Public response"
              placeholder={alreadyResponded ? 'Update your public reply' : 'Write a public reply the customer will be notified about…'}
            />
            <div className="seller-reviews__char-count">
              {response.length} / {MAX_RESPONSE}
            </div>
            {error && <p className="flash error" role="alert">{error}</p>}
            <button type="submit" className="button primary" disabled={submitting}>
              {submitting ? 'Saving…' : (alreadyResponded ? 'Save response' : 'Send response')}
            </button>
            {alreadyResponded && <button type="button" className="button secondary" onClick={() => { setEditing(false); setError(''); }}>Cancel</button>}
          </form>
        )}
      </div>
    </Modal>
  );
}
